/**
 * AssignGatewayDialog — assign an unassigned gateway to a property.
 * Used from Gateways page for devices with no property_id.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertCircle } from 'lucide-react';
import { reassignGateway } from '@/services/gateway';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';

export interface AssignGatewayDialogProps {
  deviceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssignGatewayDialog({ deviceId, open, onOpenChange }: AssignGatewayDialogProps) {
  const queryClient = useQueryClient();
  const { properties } = useApp();
  const [propertyId, setPropertyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleAssign = async () => {
    if (!propertyId) {
      setErrorMsg('Select a property');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      await reassignGateway(deviceId, propertyId);
      queryClient.invalidateQueries({ queryKey: ['gateway-devices'] });
      onOpenChange(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to assign gateway');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-wt-bg-secondary border-border">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary">Assign to property</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-wt-text-secondary">Property</Label>
            <Select value={propertyId || undefined} onValueChange={(v) => { setPropertyId(v); setErrorMsg(''); }}>
              <SelectTrigger className="bg-wt-bg-tertiary border-border">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {errorMsg && (
            <p className="text-sm text-wt-red-primary flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {errorMsg}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!propertyId || loading}>
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
